import React, { useState, useEffect } from 'react';
import { Card, Button, Header, Segment, Label, Message } from 'semantic-ui-react';
import { collection, query, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import { formatDate } from '../../../utils/dateUtils';
import ViewTest from './ViewTest';
import ViewCloseBookTest from './ViewCloseBookTest';
import GradeCloseBookList from './GradeCloseBookList';

interface BaseTest {
  id: string;
  testName: string;
  totalGrade: number;
  type: 'openBook' | 'closeBook' | 'verseMemorization';
}

interface OpenBookTest extends BaseTest {
  type: 'openBook';
  lastSubmissionDate: Date;
  questions: any[];
}

interface CloseBookTest extends BaseTest {
  type: 'closeBook';
  examStatus?: 'notStarted' | 'inProgress' | 'ended';
  criteria?: {
    shortAnswer: { description: string; grade: number };
    longAnswer: { description: string; grade: number };
  };
  questions?: {
    shortAnswer: Array<{ id: string; text: string; points: number }>;
    longAnswer: Array<{ id: string; text: string; points: number }>;
  };
  config?: {
    shortAnswer: { totalQuestions: number; requiredAnswers: number; pointsPerQuestion: number };
    longAnswer: { totalQuestions: number; requiredAnswers: number; pointsPerQuestion: number };
  };
}

interface VerseMemorizationTest extends BaseTest {
  type: 'verseMemorization';
  examStatus?: 'notStarted' | 'inProgress' | 'ended';
}

type Test = OpenBookTest | CloseBookTest | VerseMemorizationTest;

interface TestStatus {
  submissionStatus: string;
  gradingStatus: string;
  grade?: number;
}

const ViewTests: React.FC<{ moduleId: string; onExamLockChange?: (locked: boolean) => void }> = ({ moduleId, onExamLockChange }) => {
  const [tests, setTests] = useState<Test[]>([]);
  const [testStatuses, setTestStatuses] = useState<Record<string, TestStatus>>({});
  const [selectedTest, setSelectedTest] = useState<string | null>(null);
  const [isStudent, setIsStudent] = useState(false);
  const [isAdminOrTeacher, setIsAdminOrTeacher] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTestStatuses = async (fetchedTests: Test[]) => {
    const user = auth.currentUser;
    if (!user || !isStudent) return;

    const statuses: Record<string, TestStatus> = {};
    for (const test of fetchedTests) {
      const status = await fetchStudentSubmissionStatus(test.id, user.uid);
      statuses[test.id] = status;
    }
    setTestStatuses(statuses);
  };

  const fetchStudentSubmissionStatus = async (testId: string, studentId: string): Promise<TestStatus> => {
    const submissionDoc = await getDoc(doc(db, 'modules', moduleId, 'tests', testId, 'submissions', studentId));
    
    if (!submissionDoc.exists()) {
      return { submissionStatus: 'Not Submitted', gradingStatus: 'Pending' };
    }

    const submissionData = submissionDoc.data();

    // Handle open book test submission (answers is array)
    if (Array.isArray(submissionData.answers) && submissionData.answers[0]?.submitted) {
      const gradingStatus = submissionData.answers[0].gradingStatus?.toLowerCase();
      if (gradingStatus === 'graded') {
        return { submissionStatus: 'Submitted', gradingStatus: 'Graded', grade: submissionData.answers[0].grade };
      }
      return { submissionStatus: 'Submitted', gradingStatus: 'Pending' };
    }
    // Handle close book test submission (answers is object with shortAnswer/longAnswer)
    if (submissionData.answers?.shortAnswer || submissionData.answers?.longAnswer) {
      const gradingStatus = submissionData.gradingStatus?.toLowerCase();
      if (gradingStatus === 'graded') {
        return { submissionStatus: 'Submitted', gradingStatus: 'Graded', grade: submissionData.grade };
      }
      return { submissionStatus: 'Submitted', gradingStatus: 'Pending' };
    }
    // Handle verse memorization submission (answer is string)
    if (submissionData.answer !== undefined) {
      const gradingStatus = submissionData.gradingStatus?.toLowerCase();
      if (gradingStatus === 'graded') {
        return { submissionStatus: 'Submitted', gradingStatus: 'Graded', grade: submissionData.grade };
      }
      return { submissionStatus: 'Submitted', gradingStatus: 'Pending' };
    }
    // Legacy: PDF upload (close book / verse memorization)
    if (submissionData.pdfUrl) {
      const gradingStatus = submissionData.gradingStatus?.toLowerCase();
      if (gradingStatus === 'graded') {
        return { submissionStatus: 'Submitted', gradingStatus: 'Graded', grade: submissionData.grade };
      }
      return { submissionStatus: 'Submitted', gradingStatus: 'Pending' };
    }

    return { submissionStatus: 'Not Submitted', gradingStatus: 'Pending' };
  };

  useEffect(() => {
    const checkUserRoleAndFetchTests = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        // Check user role
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        const userIsStudent = userData?.role === 'student';
        const userIsAdminOrTeacher = ['admin', 'teacher'].includes(userData?.role || '');
        setIsStudent(userIsStudent);
        setIsAdminOrTeacher(userIsAdminOrTeacher);

        // Fetch tests
        const testsCollectionRef = collection(db, 'modules', moduleId, 'tests');
        const testsQuery = query(testsCollectionRef);
        const querySnapshot = await getDocs(testsQuery);

        const fetchedTests: Test[] = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            lastSubmissionDate: data.lastSubmissionDate?.toDate(),
          } as Test;
        });

        setTests(fetchedTests);
      } catch (error) {
        console.error('Error in checkUserRoleAndFetchTests:', error);
        setError('Failed to load tests');
      }
    };

    checkUserRoleAndFetchTests();
  }, [moduleId]);

  // Separate useEffect for fetching submission statuses
  useEffect(() => {
    const fetchSubmissionStatuses = async () => {
      if (!isStudent || !auth.currentUser || tests.length === 0) return;
      
      console.log('Fetching submission statuses for student:', auth.currentUser.uid);
      await fetchTestStatuses(tests);
    };

    fetchSubmissionStatuses();
  }, [isStudent, tests]);

  const handleExamStatusChange = async (
    testId: string,
    newStatus: 'notStarted' | 'inProgress' | 'ended'
  ) => {
    try {
      await updateDoc(doc(db, 'modules', moduleId, 'tests', testId), {
        examStatus: newStatus
      });

      setTests(prevTests =>
        prevTests.map(test =>
          test.id === testId && test.type !== 'openBook'
            ? { ...test, examStatus: newStatus }
            : test
        )
      );
    } catch (err) {
      console.error('Error updating exam status:', err);
      setError('Failed to update exam status');
    }
  };

  if (selectedTest) {
    const selectedTestData = tests.find(test => test.id === selectedTest);
    if (!selectedTestData) return null;

    if (selectedTestData.type === 'openBook') {
      return (
        <ViewTest
          test={selectedTestData}
          moduleId={moduleId}
          onBack={() => {
            setSelectedTest(null);
            if (isStudent) {
              fetchTestStatuses(tests);
            }
          }}
        />
      );
    } else {
      if (isStudent) {
        return (
          <ViewCloseBookTest
            test={selectedTestData}
            moduleId={moduleId}
            onBack={() => {
              setSelectedTest(null);
              if (isStudent) {
                fetchTestStatuses(tests);
              }
            }}
            onExamLockChange={onExamLockChange}
          />
        );
      } else {
        return (
          <GradeCloseBookList
            test={selectedTestData}
            moduleId={moduleId}
            onBack={() => setSelectedTest(null)}
          />
        );
      }
    }
  }

  const renderTestCard = (test: Test) => {
    const status = testStatuses[test.id] || { submissionStatus: 'Not Submitted', gradingStatus: 'Pending' };
    const examStatus = test.type !== 'openBook' ? (test as CloseBookTest | VerseMemorizationTest).examStatus : undefined;
    const examInProgress = examStatus === 'inProgress';
    const hasSubmission = status.submissionStatus === 'Submitted';
    const canStudentAccess = test.type === 'openBook' || examInProgress || hasSubmission;

    return (
      <Card fluid key={test.id}>
        <Card.Content>
          <Card.Header>
            {test.testName}
            {test.type !== 'openBook' && isStudent && (
              <Label
                color={examInProgress ? 'green' : examStatus === 'ended' ? 'grey' : 'red'}
                style={{ marginLeft: '1rem' }}
              >
                Exam {examStatus === 'inProgress' ? 'In Progress' : examStatus === 'ended' ? 'Ended' : 'Not Started'}
              </Label>
            )}
          </Card.Header>
          <Card.Meta>
            {test.type === 'openBook' ? (
              <div style={{ marginTop: '10px' }}>
                <strong>Last Submission Date:</strong> {formatDate(test.lastSubmissionDate!)}
              </div>
            ) : test.type === 'closeBook' ? (
              <div style={{ marginTop: '10px' }}>
                {(test as CloseBookTest).config ? (
                  <>
                    <div><strong>Short Answer:</strong> {(test as CloseBookTest).config!.shortAnswer.requiredAnswers} of {(test as CloseBookTest).config!.shortAnswer.totalQuestions} @ {(test as CloseBookTest).config!.shortAnswer.pointsPerQuestion} pts each</div>
                    <div><strong>Long Answer:</strong> {(test as CloseBookTest).config!.longAnswer.requiredAnswers} of {(test as CloseBookTest).config!.longAnswer.totalQuestions} @ {(test as CloseBookTest).config!.longAnswer.pointsPerQuestion} pts each</div>
                    <div><strong>Total Grade:</strong> {test.totalGrade}</div>
                  </>
                ) : (
                  <>
                    <div><strong>Short Answer Grade:</strong> {(test as CloseBookTest).criteria?.shortAnswer?.grade ?? '-'}</div>
                    <div><strong>Long Answer Grade:</strong> {(test as CloseBookTest).criteria?.longAnswer?.grade ?? '-'}</div>
                    <div><strong>Total Grade:</strong> {test.totalGrade}</div>
                  </>
                )}
              </div>
            ) : (
              <div style={{ marginTop: '10px' }}>
                <strong>Total Grade:</strong> {test.totalGrade}
              </div>
            )}
          </Card.Meta>
          {isStudent && (
            <Card.Description>
              <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '0.5rem' }}>
                <Label color='blue'>
                  Submission Status: {status.submissionStatus}
                </Label>
                {status.submissionStatus === 'Submitted' && (
                  <Label color={status.gradingStatus === 'Graded' ? 'green' : 'orange'}>
                    Grading Status: {status.gradingStatus}
                  </Label>
                )}
                {status.submissionStatus === 'Submitted' && status.gradingStatus === 'Graded' && status.grade !== undefined && (
                  <Label color='teal'>
                    Your Grade: {status.grade}/{test.totalGrade}
                  </Label>
                )}
              </div>
            </Card.Description>
          )}
        </Card.Content>
        <Card.Content extra>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              primary
              onClick={() => setSelectedTest(test.id)}
              disabled={isStudent && test.type !== 'openBook' && !canStudentAccess}
            >
              {isStudent ?
                (test.type === 'openBook' ? 'View Test' : (
                  status.gradingStatus === 'Graded' ? 'View Results' :
                  hasSubmission ? 'View Submission' : 'Take Exam'
                )) :
                (test.type === 'openBook' ? 'View/Grade Test' : 'View/Grade Submissions')
              }
            </Button>
            {isAdminOrTeacher && test.type !== 'openBook' && (
              <>
                {examStatus !== 'inProgress' && (
                  <Button
                    color="green"
                    onClick={() => handleExamStatusChange(test.id, 'inProgress')}
                  >
                    Start Exam
                  </Button>
                )}
                {examStatus === 'inProgress' && (
                  <Button
                    color="red"
                    onClick={() => handleExamStatusChange(test.id, 'ended')}
                  >
                    End Exam
                  </Button>
                )}
              </>
            )}
          </div>
        </Card.Content>
      </Card>
    );
  };

  const groupedTests = tests.reduce((acc, test) => {
    // Group both closeBook and verseMemorization under closeBook
    const groupType = test.type === 'verseMemorization' ? 'closeBook' : test.type;
    acc[groupType] = [...(acc[groupType] || []), test];
    return acc;
  }, {} as Record<string, Test[]>);

  return (
    <div>
      {error && <Message negative>{error}</Message>}
      {Object.entries(groupedTests).map(([type, testsOfType]) => (
        <Segment key={type}>
          <Header as="h3">
            {type === 'openBook' ? 'Open Book Tests' : 'Close Book Tests'}
          </Header>
          {testsOfType.map(renderTestCard)}
        </Segment>
      ))}
    </div>
  );
};

export default ViewTests;